import { queryOptions } from "@tanstack/react-query";
import {
  $getAdjudicationQueue,
  $getConflictCounts,
  $getConflictDetails,
} from "./adjudication";
import {
  $getDualReviewedHistory,
  $getDualScreeningQueue,
  $getDualScreeningStats,
  $getScreeningSettings,
  type ScreeningPhase,
} from "./dual-screening";
import {
  $getExclusionCodes,
  $getScreeningCitation,
  $getScreeningQueue,
  $getScreeningReviewedHistory,
  $getScreeningStats,
} from "./functions";
import { $getAllCitationNotes } from "./notes";
import { $getAgreementStats, $getCohensKappa, $getReviewerStats } from "./statistics";
import { $getCitationTags, $getProjectTags, $getSuggestedTags } from "./tags";

export const screeningQueueQueryOptions = (
  projectId: string,
  phase: ScreeningPhase = "title_abstract",
) =>
  queryOptions({
    queryKey: ["projects", projectId, "screening", phase, "queue"],
    queryFn: ({ signal }) => $getScreeningQueue({ signal, data: { projectId, phase } }),
    // Queue changes during review, always refetch
    staleTime: 0,
  });

export const screeningReviewedHistoryQueryOptions = (
  projectId: string,
  phase: ScreeningPhase = "title_abstract",
  limit = 20,
) =>
  queryOptions({
    queryKey: ["projects", projectId, "screening", phase, "reviewed-history", limit],
    queryFn: ({ signal }) =>
      $getScreeningReviewedHistory({ signal, data: { projectId, phase, limit } }),
    staleTime: 0,
  });

export const screeningCitationQueryOptions = (projectId: string, citationId: string) =>
  queryOptions({
    queryKey: ["projects", projectId, "screening", "citation", citationId],
    queryFn: ({ signal }) =>
      $getScreeningCitation({ signal, data: { projectId, citationId } }),
  });

export const screeningStatsQueryOptions = (
  projectId: string,
  phase: ScreeningPhase = "title_abstract",
) =>
  queryOptions({
    queryKey: ["projects", projectId, "screening", phase, "stats"],
    queryFn: ({ signal }) => $getScreeningStats({ signal, data: { projectId, phase } }),
    staleTime: 0,
  });

export const exclusionCodesQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: ["projects", projectId, "exclusion-codes"],
    queryFn: ({ signal }) => $getExclusionCodes({ signal, data: { projectId } }),
    // Exclusion codes rarely change, cache for longer
    staleTime: 5 * 60 * 1000,
  });

// Dual-screening query options

export const screeningSettingsQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: ["projects", projectId, "screening-settings"],
    queryFn: ({ signal }) => $getScreeningSettings({ signal, data: { projectId } }),
    staleTime: 5 * 60 * 1000,
  });

export const dualScreeningQueueQueryOptions = (
  projectId: string,
  phase: ScreeningPhase = "title_abstract",
) =>
  queryOptions({
    queryKey: ["projects", projectId, "dual-screening", phase, "queue"],
    queryFn: ({ signal }) =>
      $getDualScreeningQueue({ signal, data: { projectId, phase } }),
    staleTime: 0,
  });

export const dualScreeningReviewedHistoryQueryOptions = (
  projectId: string,
  phase: ScreeningPhase = "title_abstract",
  limit = 20,
) =>
  queryOptions({
    queryKey: ["projects", projectId, "dual-screening", phase, "reviewed-history", limit],
    queryFn: ({ signal }) =>
      $getDualReviewedHistory({ signal, data: { projectId, phase, limit } }),
    staleTime: 0,
  });

export const dualScreeningStatsQueryOptions = (
  projectId: string,
  phase: ScreeningPhase = "title_abstract",
) =>
  queryOptions({
    queryKey: ["projects", projectId, "dual-screening", phase, "stats"],
    queryFn: ({ signal }) =>
      $getDualScreeningStats({ signal, data: { projectId, phase } }),
    staleTime: 0,
  });

export const adjudicationQueueQueryOptions = (
  projectId: string,
  phase: ScreeningPhase = "title_abstract",
) =>
  queryOptions({
    queryKey: ["projects", projectId, "adjudication", phase, "queue"],
    queryFn: ({ signal }) =>
      $getAdjudicationQueue({ signal, data: { projectId, phase } }),
    staleTime: 0,
  });

export const conflictDetailsQueryOptions = (
  projectId: string,
  citationId: string,
  phase: ScreeningPhase = "title_abstract",
) =>
  queryOptions({
    queryKey: ["projects", projectId, "adjudication", phase, "conflict", citationId],
    queryFn: ({ signal }) =>
      $getConflictDetails({ signal, data: { projectId, citationId, phase } }),
    staleTime: 0,
  });

export const conflictCountsQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: ["projects", projectId, "conflict-counts"],
    queryFn: ({ signal }) => $getConflictCounts({ signal, data: { projectId } }),
    staleTime: 0,
  });

// Statistics query options

export const agreementStatsQueryOptions = (
  projectId: string,
  phase: ScreeningPhase = "title_abstract",
) =>
  queryOptions({
    queryKey: ["projects", projectId, "statistics", phase, "agreement"],
    queryFn: ({ signal }) => $getAgreementStats({ signal, data: { projectId, phase } }),
    staleTime: 30 * 1000, // 30 seconds
  });

export const cohensKappaQueryOptions = (
  projectId: string,
  reviewer1Id?: string,
  reviewer2Id?: string,
  phase: ScreeningPhase = "title_abstract",
) =>
  queryOptions({
    queryKey: [
      "projects",
      projectId,
      "statistics",
      phase,
      "kappa",
      reviewer1Id,
      reviewer2Id,
    ],
    queryFn: ({ signal }) =>
      $getCohensKappa({ signal, data: { projectId, reviewer1Id, reviewer2Id, phase } }),
    staleTime: 30 * 1000,
  });

export const reviewerStatsQueryOptions = (
  projectId: string,
  reviewerId?: string,
  phase: ScreeningPhase = "title_abstract",
) =>
  queryOptions({
    queryKey: ["projects", projectId, "statistics", phase, "reviewers", reviewerId],
    queryFn: ({ signal }) =>
      $getReviewerStats({ signal, data: { projectId, reviewerId, phase } }),
    staleTime: 30 * 1000,
  });

// Tags query options

export const projectTagsQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: ["projects", projectId, "tags"],
    queryFn: ({ signal }) => $getProjectTags({ signal, data: { projectId } }),
    staleTime: 0, // Tags change during review, always refetch
  });

export const suggestedTagsQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: ["projects", projectId, "tags", "suggested"],
    queryFn: ({ signal }) => $getSuggestedTags({ signal, data: { projectId } }),
    staleTime: 0,
  });

export const citationTagsQueryOptions = (projectId: string, citationId: string) =>
  queryOptions({
    queryKey: ["projects", projectId, "citations", citationId, "tags"],
    queryFn: ({ signal }) =>
      $getCitationTags({ signal, data: { projectId, citationId } }),
    staleTime: 0, // Always fresh during review
  });

// Notes query options

export const citationNotesQueryOptions = (projectId: string, citationId: string) =>
  queryOptions({
    queryKey: ["projects", projectId, "citations", citationId, "notes"],
    queryFn: ({ signal }) =>
      $getAllCitationNotes({ signal, data: { projectId, citationId } }),
    staleTime: 0,
  });
